import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, parseDecimal, parseInteger } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useSiteContent } from "../context/SiteContentContext";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, X, Instagram, ExternalLink, FileText, Save, Truck } from "lucide-react";
import ImagePicker, { resolveImageUrl } from "../components/ImagePicker";

const emptyProduct = {
  name: { fi: "", en: "", sv: "" },
  description: { fi: "", en: "", sv: "" },
  price: 0,
  image_url: "",
  additional_images: [],
  stock: 10,
  featured: false,
  category: "ceramic",
};

const emptyIg = {
  image_url: "",
  caption: "",
  post_url: "",
  order: 0,
};

const emptyShipping = {
  name: { fi: "", en: "", sv: "" },
  price: 0,
  delivery_min_days: 2,
  delivery_max_days: 5,
  countries: ["FI"],
  order: 0,
  active: true,
};

export default function AdminDashboard() {
  const { t } = useI18n();
  const { content: siteContent, refresh: refreshContent } = useSiteContent();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [igPosts, setIgPosts] = useState([]);
  const [shipping, setShipping] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingIg, setEditingIg] = useState(null);
  const [editingShip, setEditingShip] = useState(null);
  const [contentDraft, setContentDraft] = useState(null);
  const [savingContent, setSavingContent] = useState(false);

  const loadProducts = async () => {
    const r = await api.get("/products");
    setProducts(r.data);
  };
  const loadOrders = async () => {
    try {
      const r = await api.get("/admin/orders");
      setOrders(r.data);
    } catch {}
  };
  const loadIg = async () => {
    try {
      const r = await api.get("/instagram/posts");
      setIgPosts(r.data);
    } catch {}
  };
  const loadShipping = async () => {
    try {
      const r = await api.get("/admin/shipping-methods");
      setShipping(r.data);
    } catch {}
  };

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      nav("/admin/login");
      return;
    }
    api.get("/auth/me").catch(() => {
      localStorage.removeItem("admin_token");
      nav("/admin/login");
    });
    loadProducts();
    loadOrders();
    loadIg();
    loadShipping();
  }, [nav]);

  const startNew = () => setEditing({ ...emptyProduct, _isNew: true });
  const startEdit = (p) => setEditing({ ...p, additional_images: p.additional_images || [] });
  const closeEdit = () => setEditing(null);

  const startNewIg = () => setEditingIg({ ...emptyIg, _isNew: true });
  const startEditIg = (p) => setEditingIg({ ...p });
  const closeEditIg = () => setEditingIg(null);

  const startNewShip = () => setEditingShip({ ...emptyShipping, _isNew: true });
  const startEditShip = (s) => setEditingShip({ ...s });
  const closeEditShip = () => setEditingShip(null);

  const saveShip = async () => {
    const price = parseDecimal(editingShip.price);
    if (price === null || price < 0) {
      toast.error("Syötä kelvollinen hinta (esim. 5,90 tai 5.90). Ilmaiselle nouto kirjoita 0.");
      return;
    }
    const minD = parseInteger(editingShip.delivery_min_days);
    const maxD = parseInteger(editingShip.delivery_max_days);
    if (minD === null || minD < 1 || maxD === null || maxD < minD) {
      toast.error("Toimitusaika: min ≥ 1 ja max ≥ min");
      return;
    }
    const payload = {
      name: editingShip.name,
      price,
      delivery_min_days: minD,
      delivery_max_days: maxD,
      countries: (typeof editingShip.countries === "string"
        ? editingShip.countries.split(",")
        : editingShip.countries || []
      ).map((c) => c.trim().toUpperCase()).filter(Boolean),
      order: parseInteger(editingShip.order) ?? 0,
      active: editingShip.active !== false,
    };
    if (!payload.countries.length) payload.countries = ["FI"];
    try {
      if (editingShip._isNew) {
        await api.post("/admin/shipping-methods", payload);
        toast.success("Lisätty");
      } else {
        await api.put(`/admin/shipping-methods/${editingShip.id}`, payload);
        toast.success("Tallennettu");
      }
      closeEditShip();
      loadShipping();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const delShip = async (id) => {
    if (!window.confirm("Poista toimitustapa?")) return;
    try {
      await api.delete(`/admin/shipping-methods/${id}`);
      toast.success("Poistettu");
      loadShipping();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveIg = async () => {
    const payload = {
      image_url: editingIg.image_url,
      caption: editingIg.caption || "",
      post_url: editingIg.post_url,
      order: parseInt(editingIg.order) || 0,
    };
    if (!payload.image_url || !payload.post_url) {
      toast.error("Image URL and Post URL required");
      return;
    }
    try {
      if (editingIg._isNew) {
        await api.post("/admin/instagram/posts", payload);
        toast.success("Added");
      } else {
        await api.put(`/admin/instagram/posts/${editingIg.id}`, payload);
        toast.success("Saved");
      }
      closeEditIg();
      loadIg();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const delIg = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/admin/instagram/posts/${id}`);
      toast.success("Deleted");
      loadIg();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };


  const save = async () => {
    const price = parseDecimal(editing.price);
    if (price === null) {
      toast.error("Syötä kelvollinen hinta (esim. 24,90 tai 24.90)");
      return;
    }
    const stock = parseInteger(editing.stock);
    if (stock === null || stock < 0) {
      toast.error("Syötä kelvollinen varastomäärä (kokonaisluku ≥ 0)");
      return;
    }
    const payload = {
      name: editing.name,
      description: editing.description,
      price,
      image_url: editing.image_url,
      additional_images: (editing.additional_images || []).filter(Boolean),
      stock,
      featured: !!editing.featured,
      category: editing.category || "ceramic",
    };
    try {
      if (editing._isNew) {
        await api.post("/admin/products", payload);
        toast.success("Created");
      } else {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Saved");
      }
      closeEdit();
      loadProducts();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      loadProducts();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    nav("/");
  };

  // ---- Site content editor ----
  const openContentEditor = () => {
    setContentDraft(JSON.parse(JSON.stringify(siteContent)));
  };
  const setContentField = (key, l, value) => {
    setContentDraft((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [l]: value },
    }));
  };
  const saveContent = async () => {
    setSavingContent(true);
    try {
      await api.put("/admin/site-content", contentDraft);
      await refreshContent();
      toast.success("Sisältö tallennettu");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingContent(false);
    }
  };

  useEffect(() => {
    if (tab === "content" && !contentDraft && siteContent) {
      openContentEditor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, siteContent]);

  return (
    <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-16" data-testid="admin-dashboard">
      <div className="flex items-end justify-between mb-12">
        <div>
          <div className="label-caps text-muted-foreground mb-2">Pikkurumiluksia Admin</div>
          <h1 className="font-hand text-5xl md:text-6xl">{t("admin.dashboard")}</h1>
        </div>
        <button onClick={logout} className="btn-outline" data-testid="admin-logout-btn">
          {t("admin.logout")}
        </button>
      </div>

      <div className="flex gap-8 mb-10 border-b border-border">
        <button
          onClick={() => setTab("products")}
          className={`pb-4 label-caps ${tab === "products" ? "text-foreground border-b border-[hsl(var(--primary))]" : "text-muted-foreground"}`}
          data-testid="tab-products"
        >
          {t("admin.products")} ({products.length})
        </button>
        <button
          onClick={() => setTab("orders")}
          className={`pb-4 label-caps ${tab === "orders" ? "text-foreground border-b border-[hsl(var(--primary))]" : "text-muted-foreground"}`}
          data-testid="tab-orders"
        >
          {t("admin.orders")} ({orders.length})
        </button>
        <button
          onClick={() => setTab("instagram")}
          className={`pb-4 label-caps flex items-center gap-2 ${tab === "instagram" ? "text-foreground border-b border-[hsl(var(--primary))]" : "text-muted-foreground"}`}
          data-testid="tab-instagram"
        >
          <Instagram strokeWidth={2} className="w-3.5 h-3.5" />
          Instagram ({igPosts.length})
        </button>
        <button
          onClick={() => setTab("content")}
          className={`pb-4 label-caps flex items-center gap-2 ${tab === "content" ? "text-foreground border-b border-[hsl(var(--primary))]" : "text-muted-foreground"}`}
          data-testid="tab-content"
        >
          <FileText strokeWidth={2} className="w-3.5 h-3.5" />
          Sisältö
        </button>
        <button
          onClick={() => setTab("shipping")}
          className={`pb-4 label-caps flex items-center gap-2 ${tab === "shipping" ? "text-foreground border-b border-[hsl(var(--primary))]" : "text-muted-foreground"}`}
          data-testid="tab-shipping"
        >
          <Truck strokeWidth={2} className="w-3.5 h-3.5" />
          Toimitus ({shipping.length})
        </button>
      </div>

      {tab === "products" && (
        <>
          <div className="flex justify-end mb-6">
            <button onClick={startNew} className="btn-primary" data-testid="admin-add-product-btn">
              <Plus strokeWidth={1.5} className="w-4 h-4" />
              {t("admin.add_product")}
            </button>
          </div>
          <div className="border border-border bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F0EDE4]">
                <tr>
                  <th className="text-left p-4 label-caps text-muted-foreground">Image</th>
                  <th className="text-left p-4 label-caps text-muted-foreground">Name (FI)</th>
                  <th className="text-left p-4 label-caps text-muted-foreground">Price</th>
                  <th className="text-left p-4 label-caps text-muted-foreground">Stock</th>
                  <th className="text-left p-4 label-caps text-muted-foreground">Featured</th>
                  <th className="text-right p-4"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`admin-row-${p.id}`}>
                    <td className="p-4"><img src={resolveImageUrl(p.image_url)} alt="" className="w-14 h-14 object-cover" /></td>
                    <td className="p-4 font-hand text-xl">{p.name.fi}</td>
                    <td className="p-4">{p.price.toFixed(2).replace(".", ",")} €</td>
                    <td className="p-4">{p.stock}</td>
                    <td className="p-4">{p.featured ? "✓" : "—"}</td>
                    <td className="p-4 text-right space-x-2">
                      <button onClick={() => startEdit(p)} className="inline-flex items-center gap-1 hover:text-[hsl(var(--primary))]" data-testid={`edit-${p.id}`}>
                        <Pencil strokeWidth={1.25} className="w-4 h-4" />
                      </button>
                      <button onClick={() => del(p.id)} className="inline-flex items-center gap-1 hover:text-[hsl(var(--destructive))]" data-testid={`delete-${p.id}`}>
                        <Trash2 strokeWidth={1.25} className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "orders" && (
        <div className="border border-border bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F0EDE4]">
              <tr>
                <th className="text-left p-3 label-caps text-muted-foreground">Pvm</th>
                <th className="text-left p-3 label-caps text-muted-foreground">Asiakas</th>
                <th className="text-left p-3 label-caps text-muted-foreground">Yhteystiedot</th>
                <th className="text-left p-3 label-caps text-muted-foreground">Tuotteet</th>
                <th className="text-left p-3 label-caps text-muted-foreground">Osoite</th>
                <th className="text-right p-3 label-caps text-muted-foreground">Summa</th>
                <th className="text-left p-3 label-caps text-muted-foreground">Tila</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const addr = o.shipping_address || {};
                const addrLine = [
                  o.shipping_name || o.customer_name,
                  addr.line1,
                  addr.line2,
                  [(addr.postal_code || "").trim(), (addr.city || "").trim()].filter(Boolean).join(" "),
                  addr.country,
                ].filter(Boolean).join(", ");
                return (
                  <tr key={o.session_id} className="border-t border-border align-top" data-testid={`order-row-${o.session_id?.slice(-8)}`}>
                    <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{o.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3">
                      <div className="font-medium">{o.customer_name || o.shipping_name || "—"}</div>
                      <div className="text-xs text-muted-foreground font-mono">{o.session_id?.slice(-12)}</div>
                    </td>
                    <td className="p-3 text-xs">
                      {o.customer_email && <div><a className="text-[hsl(var(--accent))] hover:underline" href={`mailto:${o.customer_email}`}>{o.customer_email}</a></div>}
                      {o.customer_phone && <div><a className="text-[hsl(var(--accent))] hover:underline" href={`tel:${o.customer_phone}`}>{o.customer_phone}</a></div>}
                    </td>
                    <td className="p-3 text-xs max-w-[200px]">
                      {(o.items || []).map((it, i) => (
                        <div key={i} data-testid={`order-item-${o.id}-${i}`}>
                          {it.quantity}× {it.name}
                          {it.note && <div className="italic text-muted-foreground whitespace-pre-wrap" data-testid={`order-item-note-${o.id}-${i}`}>“{it.note}”</div>}
                        </div>
                      ))}
                    </td>
                    <td className="p-3 text-xs max-w-[220px]">{addrLine || "—"}</td>
                    <td className="p-3 text-right whitespace-nowrap">{Number(o.amount || 0).toFixed(2).replace(".", ",")} €</td>
                    <td className="p-3">
                      <span className={`label-caps text-xs ${o.payment_status === "paid" ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}`}>
                        {o.payment_status || o.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {orders.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No orders yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "instagram" && (
        <>
          <div className="flex justify-between items-center mb-6">
            <p className="text-sm text-muted-foreground">
              Lisää enintään 6 kuvan Instagram-galleria etusivulle. Kopioi kuvan URL (esim. IG-postauksen kuva) ja postauksen linkki.
            </p>
            <button onClick={startNewIg} className="btn-primary" data-testid="admin-add-ig-btn">
              <Plus strokeWidth={2} className="w-4 h-4" />
              Lisää postaus
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {igPosts.map((p) => (
              <div key={p.id} className="border border-border bg-white overflow-hidden" data-testid={`admin-ig-${p.id}`}>
                <div className="aspect-square bg-secondary">
                  <img src={resolveImageUrl(p.image_url)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-4 space-y-2">
                  <div className="text-xs text-muted-foreground line-clamp-2">{p.caption || "—"}</div>
                  <a href={p.post_url} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--primary))] inline-flex items-center gap-1 link-underline">
                    <ExternalLink strokeWidth={1.5} className="w-3 h-3" />
                    {p.post_url.length > 32 ? p.post_url.slice(0, 32) + "…" : p.post_url}
                  </a>
                  <div className="flex justify-end gap-3 pt-2 border-t border-border">
                    <button onClick={() => startEditIg(p)} data-testid={`edit-ig-${p.id}`}>
                      <Pencil strokeWidth={1.25} className="w-4 h-4 hover:text-[hsl(var(--primary))]" />
                    </button>
                    <button onClick={() => delIg(p.id)} data-testid={`delete-ig-${p.id}`}>
                      <Trash2 strokeWidth={1.25} className="w-4 h-4 hover:text-[hsl(var(--destructive))]" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {igPosts.length === 0 && (
              <div className="col-span-full p-12 text-center text-muted-foreground border border-dashed border-border">
                Ei postauksia vielä. Lisää ensimmäinen napista yllä.
              </div>
            )}
          </div>
        </>
      )}

      {tab === "content" && contentDraft && (
        <div data-testid="content-editor">
          <div className="flex justify-between items-start mb-6">
            <p className="text-sm text-muted-foreground max-w-lg">
              Muokkaa etusivun, tarinasivun ja footerin tekstejä. Tallenna nappulasta alaosassa. Muutokset näkyvät heti sivustolla.
            </p>
            <button onClick={saveContent} disabled={savingContent} className="btn-primary" data-testid="content-save-btn">
              <Save strokeWidth={2} className="w-4 h-4" />
              {savingContent ? "Tallennetaan…" : "Tallenna kaikki"}
            </button>
          </div>
          <div className="space-y-6">
            {[
              { key: "hero_eyebrow", label: "Hero — pieni yläteksti", multi: false },
              { key: "hero_title", label: "Hero — pääotsikko (rivinvaihto \\n toimii)", multi: true },
              { key: "hero_lede", label: "Hero — kuvausteksti", multi: true },
              { key: "showcase_title", label: "Kokoelma-osio — otsikko (rivinvaihto \\n)", multi: true },
              { key: "showcase_body", label: "Kokoelma-osio — kuvausteksti", multi: false },
              { key: "story_title", label: "Tarina — otsikko", multi: false },
              { key: "story_body", label: "Tarina — leipäteksti", multi: true },
              { key: "footer_address", label: "Footer — osoite (näkyy footerissa)", multi: false },
              { key: "footer_email", label: "Footer — sähköposti", multi: false },
              { key: "footer_phone", label: "Footer — puhelin", multi: false },
              { key: "business_name", label: "Yritys — nimi (Tmi)", multi: false },
              { key: "business_id", label: "Yritys — Y-tunnus", multi: false },
              { key: "business_address", label: "Yritys — täydellinen osoite (yhteystiedot/toimitusehdot)", multi: false },
              { key: "business_vat_note", label: "Yritys — ALV-huomautus", multi: true },
              { key: "terms_intro", label: "Toimitusehdot — johdanto", multi: true },
              { key: "terms_payment", label: "Toimitusehdot — maksaminen", multi: true },
              { key: "terms_delivery", label: "Toimitusehdot — toimitus", multi: true },
              { key: "terms_delivery_time", label: "Toimitusehdot — toimitusaika", multi: true },
              { key: "terms_returns", label: "Toimitusehdot — peruutus ja palautukset", multi: true },
              { key: "terms_support", label: "Toimitusehdot — asiakaspalvelu", multi: true },
            ].map(({ key, label, multi }) => (
              <div key={key} className="border border-border bg-white p-6" data-testid={`content-block-${key}`}>
                <div className="label-caps text-muted-foreground mb-4">{label}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {["fi", "en", "sv"].map((l) => (
                    <div key={l}>
                      <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">{l}</div>
                      {multi ? (
                        <textarea
                          value={contentDraft[key]?.[l] || ""}
                          onChange={(e) => setContentField(key, l, e.target.value)}
                          rows={4}
                          className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))] text-sm"
                          data-testid={`content-${key}-${l}`}
                        />
                      ) : (
                        <input
                          value={contentDraft[key]?.[l] || ""}
                          onChange={(e) => setContentField(key, l, e.target.value)}
                          className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))] text-sm"
                          data-testid={`content-${key}-${l}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-8">
            <button onClick={saveContent} disabled={savingContent} className="btn-primary" data-testid="content-save-btn-bottom">
              <Save strokeWidth={2} className="w-4 h-4" />
              {savingContent ? "Tallennetaan…" : "Tallenna kaikki"}
            </button>
          </div>
        </div>
      )}

      {tab === "shipping" && (
        <>
          <div className="flex justify-between items-start mb-6">
            <p className="text-sm text-muted-foreground max-w-lg">
              Toimitustavat näkyvät asiakkaalle Stripen kassalla — asiakas valitsee yhden, ja Stripe lisää sen automaattisesti kokonaissummaan.
            </p>
            <button onClick={startNewShip} className="btn-primary" data-testid="admin-add-shipping-btn">
              <Plus strokeWidth={2} className="w-4 h-4" />
              Lisää toimitustapa
            </button>
          </div>
          <div className="border border-border bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F0EDE4]">
                <tr>
                  <th className="text-left p-4 label-caps text-muted-foreground">Järjestys</th>
                  <th className="text-left p-4 label-caps text-muted-foreground">Nimi (FI)</th>
                  <th className="text-left p-4 label-caps text-muted-foreground">Hinta</th>
                  <th className="text-left p-4 label-caps text-muted-foreground">Toimitusaika</th>
                  <th className="text-left p-4 label-caps text-muted-foreground">Maat</th>
                  <th className="text-left p-4 label-caps text-muted-foreground">Aktiivinen</th>
                  <th className="text-right p-4"></th>
                </tr>
              </thead>
              <tbody>
                {shipping.map((s) => (
                  <tr key={s.id} className="border-t border-border" data-testid={`admin-ship-row-${s.id}`}>
                    <td className="p-4 tabular-nums">{s.order}</td>
                    <td className="p-4 font-hand text-xl">{s.name.fi}</td>
                    <td className="p-4 tabular-nums">{s.price === 0 ? "0,00 €" : `${s.price.toFixed(2).replace(".", ",")} €`}</td>
                    <td className="p-4 text-muted-foreground">{s.delivery_min_days}–{s.delivery_max_days} pv</td>
                    <td className="p-4 text-xs text-muted-foreground">{s.countries.join(", ")}</td>
                    <td className="p-4">{s.active ? "✓" : "—"}</td>
                    <td className="p-4 text-right space-x-2">
                      <button onClick={() => startEditShip(s)} data-testid={`edit-ship-${s.id}`}>
                        <Pencil strokeWidth={1.25} className="w-4 h-4 hover:text-[hsl(var(--primary))]" />
                      </button>
                      <button onClick={() => delShip(s.id)} data-testid={`delete-ship-${s.id}`}>
                        <Trash2 strokeWidth={1.25} className="w-4 h-4 hover:text-[hsl(var(--destructive))]" />
                      </button>
                    </td>
                  </tr>
                ))}
                {shipping.length === 0 && (
                  <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">Ei toimitustapoja. Lisää ensimmäinen.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editingShip && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-6" data-testid="admin-ship-modal">
          <div className="bg-white border border-border w-full max-w-2xl my-10 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="font-hand text-3xl">{editingShip._isNew ? "Uusi toimitustapa" : "Muokkaa toimitustapaa"}</h2>
              <button onClick={closeEditShip}><X strokeWidth={1.25} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {["fi", "en", "sv"].map((l) => (
                  <div key={l}>
                    <label className="label-caps text-muted-foreground block mb-2">Nimi ({l.toUpperCase()})</label>
                    <input
                      value={editingShip.name[l] || ""}
                      onChange={(e) => setEditingShip({ ...editingShip, name: { ...editingShip.name, [l]: e.target.value } })}
                      className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                      data-testid={`ship-name-${l}`}
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="label-caps text-muted-foreground block mb-2">Hinta (€)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    value={editingShip.price || ""}
                    onChange={(e) => setEditingShip({ ...editingShip, price: e.target.value })}
                    placeholder="5,90"
                    className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                    data-testid="ship-price"
                  />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground block mb-2">Min pv</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={editingShip.delivery_min_days || ""}
                    onChange={(e) => setEditingShip({ ...editingShip, delivery_min_days: e.target.value })}
                    className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                    data-testid="ship-min-days"
                  />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground block mb-2">Max pv</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={editingShip.delivery_max_days || ""}
                    onChange={(e) => setEditingShip({ ...editingShip, delivery_max_days: e.target.value })}
                    className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                    data-testid="ship-max-days"
                  />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground block mb-2">Järjestys</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={editingShip.order || ""}
                    onChange={(e) => setEditingShip({ ...editingShip, order: e.target.value })}
                    className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                    data-testid="ship-order"
                  />
                </div>
              </div>
              <div>
                <label className="label-caps text-muted-foreground block mb-2">Maat (ISO-koodit, pilkulla eroteltuna, esim. FI, SE, DE)</label>
                <input
                  value={Array.isArray(editingShip.countries) ? editingShip.countries.join(", ") : editingShip.countries}
                  onChange={(e) => setEditingShip({ ...editingShip, countries: e.target.value })}
                  className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                  placeholder="FI"
                  data-testid="ship-countries"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingShip.active !== false}
                  onChange={(e) => setEditingShip({ ...editingShip, active: e.target.checked })}
                  data-testid="ship-active"
                />
                <span className="label-caps text-muted-foreground">Aktiivinen (näytä asiakkaalle)</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-border bg-[#F0EDE4]/50">
              <button onClick={closeEditShip} className="btn-outline">Peruuta</button>
              <button onClick={saveShip} className="btn-primary" data-testid="ship-save">Tallenna</button>
            </div>
          </div>
        </div>
      )}

      {editingIg && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-6" data-testid="admin-ig-modal">
          <div className="bg-white border border-border w-full max-w-xl my-10 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="font-hand text-3xl">{editingIg._isNew ? "Lisää Instagram-postaus" : "Muokkaa postausta"}</h2>
              <button onClick={closeEditIg}><X strokeWidth={1.25} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="label-caps text-muted-foreground block mb-2">Kuva</label>
                <ImagePicker
                  value={editingIg.image_url || ""}
                  onChange={(v) => setEditingIg({ ...editingIg, image_url: v })}
                  testIdPrefix="ig-image"
                />
              </div>
              <div>
                <label className="label-caps text-muted-foreground block mb-2">Postauksen linkki (instagram.com/p/…)</label>
                <input
                  value={editingIg.post_url || ""}
                  onChange={(e) => setEditingIg({ ...editingIg, post_url: e.target.value })}
                  placeholder="https://www.instagram.com/p/xxxxx/"
                  className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                  data-testid="ig-post-url"
                />
              </div>
              <div>
                <label className="label-caps text-muted-foreground block mb-2">Kuvateksti (valinnainen)</label>
                <textarea
                  value={editingIg.caption || ""}
                  onChange={(e) => setEditingIg({ ...editingIg, caption: e.target.value })}
                  rows={2}
                  className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                  data-testid="ig-caption"
                />
              </div>
              <div>
                <label className="label-caps text-muted-foreground block mb-2">Järjestys (pienempi = ensin)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={editingIg.order || ""}
                  onChange={(e) => setEditingIg({ ...editingIg, order: e.target.value })}
                  className="w-32 bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                  data-testid="ig-order"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-border bg-[hsl(var(--muted))]/40">
              <button onClick={closeEditIg} className="btn-outline">{t("admin.cancel")}</button>
              <button onClick={saveIg} className="btn-primary" data-testid="ig-save">{t("admin.save")}</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-6" data-testid="admin-edit-modal">
          <div className="bg-white border border-border w-full max-w-2xl my-10">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="font-hand text-3xl">{editing._isNew ? t("admin.add_product") : t("admin.edit")}</h2>
              <button onClick={closeEdit}><X strokeWidth={1.25} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {["fi", "en", "sv"].map((l) => (
                  <div key={l}>
                    <label className="label-caps text-muted-foreground block mb-2">{t(`admin.name_${l}`)}</label>
                    <input
                      value={editing.name[l] || ""}
                      onChange={(e) => setEditing({ ...editing, name: { ...editing.name, [l]: e.target.value } })}
                      className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                      data-testid={`edit-name-${l}`}
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {["fi", "en", "sv"].map((l) => (
                  <div key={l}>
                    <label className="label-caps text-muted-foreground block mb-2">{t(`admin.desc_${l}`)}</label>
                    <textarea
                      value={editing.description[l] || ""}
                      onChange={(e) => setEditing({ ...editing, description: { ...editing.description, [l]: e.target.value } })}
                      rows={3}
                      className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                      data-testid={`edit-desc-${l}`}
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="label-caps text-muted-foreground block mb-2">{t("admin.price")}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    value={editing.price || ""}
                    onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                    placeholder="24,90"
                    className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                    data-testid="edit-price"
                  />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground block mb-2">{t("admin.stock")}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={editing.stock || ""}
                    onChange={(e) => setEditing({ ...editing, stock: e.target.value })}
                    placeholder="10"
                    className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                    data-testid="edit-stock"
                  />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground block mb-2">Category</label>
                  <input
                    value={editing.category || ""}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="w-full bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
                    data-testid="edit-category"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 pb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editing.featured}
                      onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                      data-testid="edit-featured"
                    />
                    <span className="label-caps text-muted-foreground">{t("admin.featured")}</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="label-caps text-muted-foreground block mb-2">Pääkuva</label>
                <ImagePicker
                  value={editing.image_url || ""}
                  onChange={(v) => setEditing({ ...editing, image_url: v })}
                  testIdPrefix="edit-image"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label-caps text-muted-foreground">Lisäkuvat (galleria)</label>
                  <button
                    type="button"
                    onClick={() => setEditing({
                      ...editing,
                      additional_images: [...(editing.additional_images || []), ""],
                    })}
                    className="text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1"
                    data-testid="edit-add-additional-image"
                  >
                    <Plus strokeWidth={2} className="w-3 h-3" />
                    Lisää kuva
                  </button>
                </div>
                {(!editing.additional_images || editing.additional_images.length === 0) && (
                  <div className="text-xs text-muted-foreground p-4 border border-dashed border-border">
                    Ei lisäkuvia — klikkaa yllä "Lisää kuva" lisätäksesi tuotteelle useampi kuva. Tuotesivulla näkyy pääkuva + galleriapikkukuvat.
                  </div>
                )}
                <div className="space-y-4">
                  {(editing.additional_images || []).map((url, i) => (
                    <div key={i} className="flex gap-3 items-start" data-testid={`additional-image-row-${i}`}>
                      <div className="flex-1">
                        <ImagePicker
                          value={url}
                          onChange={(v) => {
                            const next = [...(editing.additional_images || [])];
                            next[i] = v;
                            setEditing({ ...editing, additional_images: next });
                          }}
                          testIdPrefix={`edit-additional-${i}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const next = (editing.additional_images || []).filter((_, j) => j !== i);
                          setEditing({ ...editing, additional_images: next });
                        }}
                        className="text-muted-foreground hover:text-[hsl(var(--destructive))] mt-2"
                        data-testid={`remove-additional-image-${i}`}
                        aria-label="Poista"
                      >
                        <Trash2 strokeWidth={1.25} className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-border bg-[#F0EDE4]/50">
              <button onClick={closeEdit} className="btn-outline" data-testid="edit-cancel">{t("admin.cancel")}</button>
              <button onClick={save} className="btn-primary" data-testid="edit-save">{t("admin.save")}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
